import { useCallback, useEffect, useRef, useState } from "react";
import { interpolateFrame } from "./frames";
import { DEFAULT_FRAME_DURATION } from "./constants";

/**
 * Animation playback hook for tactical frames.
 * Returns isPlaying, previewFrame (interpolated), segmentIdx, play, stop.
 */
export function usePlayback(frames) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [previewFrame, setPreviewFrame] = useState(null);
  const [segmentIdx, setSegmentIdx] = useState(0);
  const stateRef = useRef({ raf: 0, started: 0 });

  const stop = useCallback(() => {
    cancelAnimationFrame(stateRef.current.raf);
    setIsPlaying(false);
    setPreviewFrame(null);
    setSegmentIdx(0);
  }, []);

  const play = useCallback(() => {
    if (frames.length < 2) return false;
    setIsPlaying(true);
    setSegmentIdx(0);
    let currentSegment = 0;
    stateRef.current.started = performance.now();

    const tick = (now) => {
      const fa = frames[currentSegment];
      const fb = frames[currentSegment + 1];
      if (!fa || !fb) {
        setPreviewFrame(null);
        setIsPlaying(false);
        return;
      }
      const elapsed = now - stateRef.current.started;
      const dur = fa.duration_ms || DEFAULT_FRAME_DURATION;
      const t = Math.min(1, elapsed / dur);
      setPreviewFrame(interpolateFrame(fa, fb, t));
      if (t >= 1) {
        const nextIdx = currentSegment + 1;
        if (nextIdx >= frames.length - 1) {
          setPreviewFrame(fb);
          setTimeout(() => {
            setPreviewFrame(null);
            setIsPlaying(false);
            setSegmentIdx(0);
          }, 200);
          return;
        }
        currentSegment = nextIdx;
        setSegmentIdx(nextIdx);
        stateRef.current.started = now;
      }
      stateRef.current.raf = requestAnimationFrame(tick);
    };
    stateRef.current.raf = requestAnimationFrame(tick);
    return true;
  }, [frames]);

  useEffect(() => () => cancelAnimationFrame(stateRef.current.raf), []);

  return { isPlaying, previewFrame, segmentIdx, play, stop };
}
